/**
 * usePhotoGroups — группы фото «До/После» Работы. По форме — useInspectionRefs.js,
 * но с одним отличием: группы Объекта грузятся сразу (нужны, чтобы решить,
 * рисовать ли секции), а Наборы Орги — лениво, по требованию (шторка «Набор»),
 * чтобы не тянуть их на каждое открытие обычной Работы без единой группы.
 *
 * ⚠️ Как и у areas: список групп не кэшируется дольше открытого экрана — двое
 * на одном Объекте могут одновременно завести/переименовать группу.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { track } from '../analytics';

export function usePhotoGroups({ propertyId, enabled = true }) {
  const [groups, setGroups] = useState([]);
  const [sets, setSets] = useState(null); // null = ещё не грузили (лениво)
  const [canEditGroups, setCanEditGroups] = useState(false);
  const [canEditSets, setCanEditSets] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const setsLoadingRef = useRef(false);

  const loadGroups = useCallback(async () => {
    const r = await api.get(`/properties/${propertyId}/photo-groups`);
    setGroups(r.groups || []);
    setCanEditGroups(!!r.can_edit);
    return r.groups || [];
  }, [propertyId]);

  useEffect(() => {
    if (!enabled || !propertyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadGroups();
        if (!cancelled) setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'photo_groups');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, propertyId, loadGroups]);

  /** Наборы Орги — грузятся по требованию (первое открытие шторки «Набор»). */
  const loadSets = useCallback(async () => {
    if (setsLoadingRef.current) return sets || [];
    setsLoadingRef.current = true;
    try {
      const r = await api.get('/photo-group-sets');
      setSets(r.sets || []);
      setCanEditSets(!!r.can_edit);
      if (r.seeded) track('photo_group_sets_seeded', { count: (r.sets || []).length });
      return r.sets || [];
    } finally {
      setsLoadingRef.current = false;
    }
  }, [sets]);

  // ── Группы объекта ───────────────────────────────────────────────────────
  const createGroup = useCallback(async (label) => {
    const r = await api.post(`/properties/${propertyId}/photo-groups`, { label_ru: label });
    track('photo_group_add', { action: r.created ? 'add' : 'reuse', property_id: propertyId });
    await loadGroups();
    return r.group;
  }, [propertyId, loadGroups]);

  const renameGroup = useCallback(async (groupId, label) => {
    const row = await api.put(`/properties/${propertyId}/photo-groups/${groupId}`, { label_ru: label });
    track('photo_group_rename', { property_id: propertyId });
    setGroups(prev => prev.map(g => (g.id === groupId ? { ...g, ...row } : g)));
    return row;
  }, [propertyId]);

  const archiveGroup = useCallback(async (groupId) => {
    await api.delete(`/properties/${propertyId}/photo-groups/${groupId}`);
    track('photo_group_archive', { property_id: propertyId });
    setGroups(prev => prev.filter(g => g.id !== groupId));
  }, [propertyId]);

  const reorderGroups = useCallback(async (order) => {
    setGroups(prev => {
      const byId = new Map(order.map(o => [o.id, o.sort_order]));
      return [...prev].sort((a, b) => (byId.get(a.id) ?? a.sort_order) - (byId.get(b.id) ?? b.sort_order));
    });
    const r = await api.patch(`/properties/${propertyId}/photo-groups/reorder`, { order });
    track('photo_group_reorder', { property_id: propertyId });
    setGroups(r.groups || []);
  }, [propertyId]);

  /** Материализовать Набор на Объекте — аддитивно к уже существующим группам. */
  const applySet = useCallback(async (setId) => {
    const r = await api.post(`/properties/${propertyId}/photo-groups/apply-set`, { set_id: setId });
    track('photo_group_apply_set', { property_id: propertyId, set_id: setId, added: r.added });
    setGroups(r.groups || []);
    return r;
  }, [propertyId]);

  return {
    groups, sets, loading, error,
    canEditGroups, canEditSets,
    reloadGroups: loadGroups, loadSets, reloadSets: loadSets,
    createGroup, renameGroup, archiveGroup, reorderGroups, applySet,
  };
}

/** Ярлык группы/Набора на языке интерфейса — 1:1 с refLabel из useInspectionRefs. */
export function photoGroupLabel(row, lang) {
  if (!row) return '';
  return (lang === 'en' ? (row.label_en || row.label_ru) : (row.label_ru || row.label_en)) || '';
}
